import { useEffect, useRef, useState } from 'react';
import useServicesAvailability from '../queries/services/useServicesAvailability';
import useCalendarPaginateNavigation from '@/hooks/useCalendarPaginateNavigation';
import { useRootStore } from '@/providers/root-store-provider';
import { parseDate, toMonth } from '@/utils/date';
import { UseFormReturn } from 'react-hook-form';
import { SCHEDULE_STATUS } from '@/utils/schedule';

type TFormChooseDatetime = {
  serviceId: string;
  form: UseFormReturn<any>;
};

export const useFormChooseDatetime = ({
  serviceId,
  form
}: TFormChooseDatetime) => {
  const calendarPaginateNavigation = useCalendarPaginateNavigation();
  const { month, year } = calendarPaginateNavigation;

  const {
    data: availableDates,
    isError,
    isPending
  } = useServicesAvailability({
    serviceId,
    participants: 1,
    month,
    year
  });

  const stepServicesData = useRootStore((store) => store.stepServicesData); // ???
  const removeStep = useRootStore((store) => store.removeStep);
  const currentStep = useRootStore((store) => store.currentStep);
  const loadStepServicesData = useRootStore(
    (store) => store.loadStepServicesData
  );
  const [timeIsLoading, setTimeIsLoading] = useState(false);
  const continueRef = useRef<null | HTMLButtonElement>(null);

  const dateValue = form.getValues('date');

  // get the available dates and sort them
  const schedule = (availableDates ?? [])
    .map((date) => ({ ...date, date: parseDate(date.date) }))
    .filter((date) =>
      date.slots?.some((slot) => slot.status === SCHEDULE_STATUS.available)
    );
  const findSchedule = (date: Date) => {
    return schedule?.find(
      (value) => value?.date?.getDate() === date?.getDate()
    );
  };

  const hasSchedule = !!schedule.length;
  const hasUnavailableSchedule = findSchedule(dateValue)?.slots.some(
    (slot) => slot.status !== SCHEDULE_STATUS.available
  );

  const defaultDate = () => {
    const isValidCurrentDate = stepServicesData?.firstReservationStartTime;
    if (isValidCurrentDate) {
      return new Date(isValidCurrentDate);
    }
    if (hasSchedule) {
      return schedule[0].date;
    }
    return undefined;
  };

  const handleDateChange = () => {
    // Hide the time field before showing it again to force re-render
    setTimeIsLoading(true);
    setTimeout(() => {
      setTimeIsLoading(false);
    }, 300); // Using a delay of 0ms for quick toggle
  };

  const scrollToContinue = (ref: React.RefObject<HTMLButtonElement>) => {
    requestAnimationFrame(() => {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth' });
      }
    });
  };

  useEffect(() => {
    loadStepServicesData({
      firstReservationStartTime: undefined
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    availableDates,
    calendarPaginateNavigation,
    stepServicesData,
    toMonth,
    schedule,
    dateValue,
    continueRef,
    currentStep,
    isError,
    isPending,
    timeIsLoading,
    hasSchedule,
    hasUnavailableSchedule,
    handleDateChange,
    defaultDate,
    findSchedule,
    scrollToContinue,
    removeStep,
    loadStepServicesData
  };
};
